import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: number;
  type: ToastType;
  message: string;
  duration: number;
}

const DEFAULT_DURATION_MS = 4500;

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private readonly toastsSubject = new BehaviorSubject<Toast[]>([]);
  readonly toasts$: Observable<Toast[]> = this.toastsSubject.asObservable();

  private nextId = 0;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  success(message: string, duration: number = DEFAULT_DURATION_MS): void {
    this.show('success', message, duration);
  }

  error(message: string, duration: number = DEFAULT_DURATION_MS): void {
    this.show('error', message, duration);
  }

  warning(message: string, duration: number = DEFAULT_DURATION_MS): void {
    this.show('warning', message, duration);
  }

  info(message: string, duration: number = DEFAULT_DURATION_MS): void {
    this.show('info', message, duration);
  }

  remove(id: number): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    this.toastsSubject.next(
      this.toastsSubject.value.filter((toast) => toast.id !== id),
    );
  }

  private show(type: ToastType, message: string, duration: number): void {
    const toast: Toast = {
      id: this.nextId++,
      type,
      message,
      duration,
    };

    this.toastsSubject.next([...this.toastsSubject.value, toast]);

    const timer = setTimeout(() => this.remove(toast.id), duration);
    this.timers.set(toast.id, timer);
  }
}
